import React, { useState } from 'react';
import { CalendarEvent } from './../../../../types/calendar/calendarTypes';
import Label from './../../yourProperties/Label';
import Icon from './../../../../muiComponents/Icon';
import IconButton from './../../../../muiComponents/IconButton';
import { Popover } from '@mui/material';
import SectionTitle from './../../yourProperties/Title';
import Title from './../../../components/Title';
import useTranslate from './../../../../customHooks/useTranslate';
import { formatDate, isDateless } from './../../../../utils/formatDates';
import Grid from './../../../../muiComponents/Grid';
import GridChild from './../../../../muiComponents/GridChild';
import Tooltip from './../../../../muiComponents/Tooltip';
import { ITaxState } from '../../../../types/taxes/taxesTypes';
import DotIcon from '../../../components/DotIcon';
import { useSearchParams } from '../../../../customHooks/useSearchParams';
import SupplierTaxTypeIcon from '../taxTypesIcons/SupplierTaxTypeIcon';
import SupplierTaxCalculationTypeIcon from '../taxTypesIcons/SupplierTaxCalculationTypeIcon';
import SupplierTaxCalculationRuleTypeSubjectIcon from '../taxTypesIcons/SupplierTaxCalculationRuleTypeIcon';
import routes from '../../../../utils/commonVariables/routes';
import { commonIcons } from '../../../../utils/commonVariables/commonIcons';
import {
    doubleMD_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
} from '../../../../utils/commonVariables/grid';

interface EventProps {
    event: CalendarEvent<ITaxState>;
    idHoveredEvent: string | null;
    hoverEvent: (eventId: string) => void;
    unhoverEvent: () => void;
}

const TaxEvent = ({
    event,
    idHoveredEvent,
    hoverEvent,
    unhoverEvent,
}: EventProps) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const translate = useTranslate();

    const {
        Id,
        Enabled,
        Name,
        Description,
        FromDate,
        ToDate,
        IdSupplierHotel,
        IdSupplierHotelRoomType,
        IdSupplierHotelMealPlan,
        IdSupplierHotelRatePlan,
        SupplierHotelRoomType,
        SupplierHotelMealPlan,
        SupplierHotelRatePlan,
        SupplierHotel,
        SupplierTaxTypeKey,
        SupplierTaxCalculationTypeKey,
        SupplierTaxCalculationRuleTypeKey,
        SupplierTaxType,
        SupplierTaxCalculationType,
    } = event.extendedProps;

    const { setSearchParam } = useSearchParams({ base64: true });

    const handleShowDetails = (idTax: string) => {
        setAnchor(null);
        setSearchParam('id', idTax);
    };

    const datelessFrom = isDateless(FromDate);
    const datelessTo = isDateless(ToDate);

    return (
        <>
            <div
                className={`overflow-hidden flex flex-between gap-5 flex-align-center border-radius-4 cursor-pointer padding-4 ${
                    idHoveredEvent === event.id
                        ? 'fc__hovered-event'
                        : 'border-color'
                }`}
                style={{ height: '30px', maxWidth: '100%' }}
                onClick={() => {
                    handleShowDetails(Id);
                }}
                onMouseEnter={() => hoverEvent(event.id)}
                onMouseLeave={unhoverEvent}
            >
                <div className='flex gap-5 flex-align-center overflow-hidden'>
                    <DotIcon truthy={Enabled} falsy={!Enabled} />

                    <Label text={Name} translated={false} />
                </div>
                <IconButton
                    iconName={anchor ? 'VisibilityOff' : 'Visibility'}
                    onClick={(
                        iconEvent: React.MouseEvent<
                            HTMLButtonElement,
                            MouseEvent
                        >
                    ) => {
                        iconEvent.stopPropagation();
                        setAnchor(anchor ? null : iconEvent.currentTarget);
                    }}
                    style={{ padding: '4px' }}
                    iconProps={{ style: { fontSize: '16px' } }}
                />
            </div>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    paper: {
                        borderRadius: '16px',
                    },
                }}
            >
                <div
                    style={{ width: '600px' }}
                    className='flex-column gap-20 padding20'
                >
                    <div className='flex flex-between flex-align-baseline padding-inline15 border-color-bottom padding-bottom-8'>
                        <SectionTitle
                            className='text-secondary-color'
                            text={Name}
                            translated={false}
                        />
                        <div className='flex gap-20 flex-align-center'>
                            {!datelessFrom || !datelessTo ? (
                                <p>
                                    {FromDate
                                        ? formatDate(FromDate)
                                        : '--/--/----'}
                                    {' - '}
                                    {ToDate ? formatDate(ToDate) : '--/--/----'}
                                </p>
                            ) : (
                                ''
                            )}
                            <Tooltip
                                title={translate(
                                    Enabled ? 'enabled' : 'disabled'
                                )}
                            >
                                <span>
                                    <DotIcon
                                        truthy={Enabled}
                                        falsy={!Enabled}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                    <div className='flex-column gap-5 padding-inline15'>
                        <div className='flex flex-end width100perc'>
                            <div className='flex gap-10'>
                                <SupplierTaxTypeIcon
                                    entityKey={SupplierTaxTypeKey}
                                    text={SupplierTaxType}
                                />
                                <SupplierTaxCalculationTypeIcon
                                    entityKey={SupplierTaxCalculationTypeKey}
                                    text={SupplierTaxCalculationType}
                                />
                                <SupplierTaxCalculationRuleTypeSubjectIcon
                                    entityKey={
                                        SupplierTaxCalculationRuleTypeKey
                                    }
                                    type='subject'
                                />
                                <SupplierTaxCalculationRuleTypeSubjectIcon
                                    entityKey={
                                        SupplierTaxCalculationRuleTypeKey
                                    }
                                    type='frequency'
                                />
                            </div>
                        </div>
                        <Tooltip title={Description || ''}>
                            <p className='tax__description text-preline'>
                                {Description || ''}
                            </p>
                        </Tooltip>
                    </div>
                    <Grid
                        style={{ width: 'auto', margin: 0 }}
                        className='border-color-block'
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing20}
                    >
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <div className='flex-column gap-5'>
                                <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                    <Icon
                                        iconName={commonIcons.HOTEL}
                                        className='text-secondary-700'
                                    />
                                    <Title className='text-capitalize'>
                                        {translate('hotel')}
                                    </Title>
                                </div>
                                <Tooltip titleText='navigate_to_hotel_detail'>
                                    <p
                                        className='link-onhover cursor-pointer '
                                        onClick={() => {
                                            const route = `${routes.propertyDetail(
                                                IdSupplierHotel
                                            )}`;
                                            window.open(route, route);
                                        }}
                                    >
                                        {SupplierHotel}
                                    </p>
                                </Tooltip>
                            </div>
                        </GridChild>
                        {IdSupplierHotelRoomType ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <div className='flex-column gap-5'>
                                    <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                        <Icon
                                            iconName='Bed'
                                            className='text-secondary-700'
                                        />
                                        <Title>{translate('room')}</Title>
                                    </div>
                                    <p>
                                        {SupplierHotelRoomType ||
                                            translate('not_specified')}
                                    </p>
                                </div>
                            </GridChild>
                        ) : null}

                        {IdSupplierHotelMealPlan ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <div className='flex-column gap-5'>
                                    <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                        <Icon
                                            iconName='RoomService'
                                            className='text-secondary-700'
                                        />
                                        <Title className='text-capitalize'>
                                            {translate('meal_plan')}
                                        </Title>
                                    </div>
                                    <p>
                                        {SupplierHotelMealPlan ||
                                            translate('not_specified')}
                                    </p>
                                </div>
                            </GridChild>
                        ) : null}
                        {IdSupplierHotelRatePlan ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <div className='flex-column gap-5'>
                                    <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                        <Icon
                                            iconName='SellOutlined'
                                            className='text-secondary-700'
                                        />
                                        <Title className='text-capitalize'>
                                            {translate('rate_plan')}
                                        </Title>
                                    </div>
                                    <p>
                                        {SupplierHotelRatePlan ||
                                            translate('not_specified')}
                                    </p>
                                </div>
                            </GridChild>
                        ) : null}
                    </Grid>

                    <div
                        className='padding-inline15 gap-10 flex flex-baseline text-primary-400 cursor-pointer'
                        onClick={() => handleShowDetails(Id)}
                    >
                        <Icon iconName='Description' />
                        {translate('go_to_tax_detail')}
                    </div>
                </div>
            </Popover>
        </>
    );
};

export default TaxEvent;
